import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Navigation, CheckCircle2, Loader2, AlertTriangle, MapPinned } from 'lucide-react';
import { toast } from 'sonner';
import api, { getAccessToken } from '../lib/api';

const REFRESH_MS = 10_000;

/**
 * "I'm on my way" → "I've arrived" button block for the trainer's session card.
 * Opens a WebSocket and uses navigator.geolocation.watchPosition to push updates
 * every ~10s. Falls back to a no-GPS toggle if the browser denies permission.
 */
const TrainerEnRouteButton = ({ session, onChange }) => {
  const [enRoute, setEnRoute] = useState(!!session.trainer_en_route);
  const [busy, setBusy] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastSentRef = useRef(0);

  const cleanup = useCallback(() => {
    if (watchIdRef.current && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_) { /* noop */ }
    }
    watchIdRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (_) { /* noop */ }
      wsRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const buildWsUrl = () => {
    const httpUrl = process.env.REACT_APP_BACKEND_URL;
    if (!httpUrl) return null;
    const wsUrl = httpUrl.replace(/^http/, 'ws');
    const token = getAccessToken();
    return `${wsUrl}/api/ws/track/${session.id}?token=${encodeURIComponent(token || '')}`;
  };

  const openSocket = () => {
    const url = buildWsUrl();
    if (!url) return null;
    const ws = new WebSocket(url);
    ws.onerror = () => { /* silent — REST fallback covers it */ };
    return ws;
  };

  const broadcastLocation = async (lat, lng) => {
    const now = Date.now();
    if (now - lastSentRef.current < 4_000) return;  // throttle
    lastSentRef.current = now;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'location', lat, lng }));
    } else {
      try {
        await api.post(`/sessions/${session.id}/tracking/update`, { lat, lng });
      } catch (e) {
        // ignore — best effort
      }
    }
  };

  const startTracking = async () => {
    setBusy(true);
    setPermissionDenied(false);

    if (!('geolocation' in navigator)) {
      // No GPS at all — server-side flag only, no map
      try {
        await api.post(`/sessions/${session.id}/tracking/start`, { lat: 0, lng: 0 });
        setEnRoute(true);
        setPermissionDenied(true);
        onChange?.({ trainer_en_route: true });
        toast.message('You\u2019re marked on the way. Location sharing unavailable.');
      } catch (e) {
        toast.error('Could not start tracking');
      } finally {
        setBusy(false);
      }
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          await api.post(`/sessions/${session.id}/tracking/start`, { lat, lng });
        } catch (e) {
          toast.error('Could not start tracking');
          setBusy(false);
          return;
        }
        setEnRoute(true);
        onChange?.({ trainer_en_route: true });

        // Open WS for fan-out
        wsRef.current = openSocket();

        // Push immediately
        broadcastLocation(lat, lng);

        // Continuous watcher
        watchIdRef.current = navigator.geolocation.watchPosition(
          (p) => broadcastLocation(p.coords.latitude, p.coords.longitude),
          () => { /* swallow — REST fallback retries */ },
          { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 }
        );

        // Safety re-broadcast on a timer (in case watchPosition stalls)
        intervalRef.current = setInterval(() => {
          navigator.geolocation.getCurrentPosition(
            (p) => broadcastLocation(p.coords.latitude, p.coords.longitude),
            () => {},
            { maximumAge: 8_000, timeout: 10_000 }
          );
        }, REFRESH_MS);

        toast.success('Client can see you on the map now.');
        setBusy(false);
      },
      async (err) => {
        // Permission denied or unavailable — fall back to flag-only
        setPermissionDenied(true);
        try {
          await api.post(`/sessions/${session.id}/tracking/start`, { lat: 0, lng: 0 });
          setEnRoute(true);
          onChange?.({ trainer_en_route: true });
          toast.message('You\u2019re marked on the way. (Allow location to share live updates.)');
        } catch (e2) {
          toast.error('Could not start tracking');
        } finally {
          setBusy(false);
        }
      },
      { enableHighAccuracy: true, timeout: 15_000 }
    );
  };

  const stopTracking = async () => {
    setBusy(true);
    try {
      // Signal arrival over WS if open, otherwise REST
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'arrived' }));
      }
      await api.post(`/sessions/${session.id}/tracking/stop`);
      setEnRoute(false);
      onChange?.({ trainer_en_route: false });
      cleanup();
      toast.success('Marked arrived.');
    } catch (e) {
      toast.error('Could not update');
    } finally {
      setBusy(false);
    }
  };

  if (!enRoute) {
    return (
      <Button
        type="button"
        onClick={startTracking}
        disabled={busy}
        className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
        data-testid="trainer-en-route-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Navigation className="w-4 h-4 mr-2" />}
        I&apos;m on my way
      </Button>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="trainer-tracking-active">
      <Button
        type="button"
        onClick={stopTracking}
        disabled={busy}
        variant="outline"
        className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
        data-testid="trainer-arrived-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
        I&apos;ve arrived
      </Button>
      {permissionDenied ? (
        <span className="text-xs text-amber-300 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Sharing without GPS
        </span>
      ) : (
        <span className="text-xs text-emerald-300 flex items-center gap-1">
          <MapPinned className="w-3 h-3" /> Live location streaming
        </span>
      )}
    </div>
  );
};

export default TrainerEnRouteButton;
